import React from "react";
import { Question, QuestionsMode, UserRole } from "../../types";
import QuestionItem from "./QuestionItem";
import { useAuth } from "../../contexts/AuthContext";
import { FileText, Loader2, Plus } from "lucide-react";

type Props = {
  questions: Question[];
  language: "es" | "ca" | "en" | "ar";
  currentPlayingId: string | null;
  setCurrentPlayingId: (id: string | null) => void;
  audioRefs: React.MutableRefObject<Record<string, HTMLAudioElement | null>>;
  handleEditQuestion: (q: Question) => void;
  handleDeleteQuestion: (questionId: string) => void;
  moveQuestion: (id: string, direction: "up" | "down") => void;
  setNotification: React.Dispatch<
    React.SetStateAction<{
      type: "success" | "error" | "warning" | "info";
      title: string;
      message?: string;
      actions?: import("../Notification").NotificationAction[];
    } | null>
  >;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  mode: QuestionsMode;
  isLoading?: boolean;
  error?: unknown;
};

export default function QuestionsList({
  mode,
  questions,
  language,
  currentPlayingId,
  setCurrentPlayingId,
  audioRefs,
  handleEditQuestion,
  handleDeleteQuestion,
  moveQuestion,
  setNotification,
  setShowForm,
  isLoading,
  error,
}: Props) {
  const { translations, role } = useAuth();
  const isGeneric = mode.kind === "generic";
  const lockedForOrganization = role === UserRole.Organization && isGeneric;
  const title = isGeneric
    ? translations.genericQuestions
    : translations.specificQuestions ?? translations.genericQuestions;

  const emptyTitle = isGeneric
    ? translations.noGenericQuestions
    : translations.noSpecificQuestions ?? translations.noGenericQuestions;

  const emptySubtitle = isGeneric
    ? translations.startAddingFirstGenericQuestion
    : translations.startAddingFirstSpecificQuestion ??
      translations.startAddingFirstGenericQuestion;
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
          <div className="p-12 flex flex-col items-center justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
            <p className="mt-4 text-gray-600">
              {translations.loadingQuestions ?? "Cargando preguntas..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
          <div className="p-12 flex flex-col items-center justify-center text-center">
            <FileText className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {translations.errorLoadingQuestions ??
                "Error al cargar las preguntas"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {error instanceof Error
                ? error.message
                : translations.unexpectedError ??
                  "Ha ocurrido un error inesperado."}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {translations.retry ?? "Reintentar"}
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {title} ({questions.length})
        </h2>

        <div className="space-y-4">
          {questions.map((question, index) => (
            <QuestionItem
              mode={mode}
              questions={questions}
              moveQuestion={moveQuestion}
              key={question.id}
              question={question}
              index={index}
              total={questions.length}
              language={language}
              currentPlayingId={currentPlayingId}
              setCurrentPlayingId={setCurrentPlayingId}
              audioRefs={audioRefs}
              handleEditQuestion={() => handleEditQuestion(question)}
              handleDeleteQuestion={() => handleDeleteQuestion(question.id)}
              setNotification={setNotification}
            />
          ))}
        </div>

        {questions.length === 0 && (
          <div className="text-center py-12">
            {!lockedForOrganization ? (
              <>
                <div
                  className="cursor-pointer w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4"
                  onClick={() => setShowForm(true)}
                >
                  <Plus className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900">
                  {emptyTitle}
                </h3>
                <p className="text-sm text-gray-500 mt-1">{emptySubtitle}</p>
              </>
            ) : (
              <>
                <h3 className="text-sm font-medium text-gray-900">
                  {translations.noGenericQuestions}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {translations.genericQuestionsAreManagedGlobally ??
                    "Las preguntas genéricas se gestionan a nivel global. No es posible crear ni editar desde este rol."}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
